import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart2, ChevronDown, ChevronRight, Clock, ListChecks, TimerReset, Users } from 'lucide-react'
import { workloadApi } from '../api/workload'
import { Avatar } from '../components/common/Avatar'
import type { Project, ProjectValueDashboard, ProjectValueEmployee } from '../types'

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const addDaysInput = (date: Date, amount: number) => {
  const next = new Date(date)
  next.setDate(next.getDate() + amount)
  const month = String(next.getMonth() + 1).padStart(2, '0')
  const day = String(next.getDate()).padStart(2, '0')
  return `${next.getFullYear()}-${month}-${day}`
}

const formatHours = (hours: number) => {
  if (!Number.isFinite(hours) || Math.abs(hours) < 0.01) return '0h'
  const sign = hours < 0 ? '-' : ''
  const value = Math.abs(hours)
  const wholeHours = Math.floor(value)
  const minutes = Math.round((value - wholeHours) * 60)
  if (minutes === 60) return `${sign}${wholeHours + 1}h`
  return minutes > 0 ? `${sign}${wholeHours}h ${minutes}m` : `${sign}${wholeHours}h`
}

const healthLabel = (health: ProjectValueEmployee['health']) => {
  if (health === 'OVER') return 'Over'
  if (health === 'ON_TRACK') return 'On track'
  if (health === 'UNDER') return 'Under'
  return 'No data'
}

const healthColor = (health: ProjectValueEmployee['health']) => {
  if (health === 'OVER') return '#dc2626'
  if (health === 'ON_TRACK') return '#16a34a'
  if (health === 'UNDER') return '#ca8a04'
  return '#64748b'
}

export default function ProjectValuesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['project-values-projects'],
    queryFn: () => workloadApi.listProjectValueProjects(),
  })

  const projects = data?.data?.data || []

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Selected projects</div>
          <h1 style={titleStyle}>Project Values</h1>
          <p style={subtitleStyle}>
            Employee estimated hours versus actual logged time for projects enabled in the project add or edit popup.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div style={emptyStyle}>Loading project values...</div>
      ) : projects.length === 0 ? (
        <div style={emptyStyle}>
          No projects are enabled yet. Open a project add or edit popup and tick Show in Project Values.
        </div>
      ) : (
        <div style={projectStackStyle}>
          {projects.map((project) => (
            <ProjectValueSection key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  )
}

function ProjectValueSection({
  project,
}: {
  project: Pick<Project, 'id' | 'name' | 'identifier' | 'color' | 'description' | 'showOnDashboard'>
}) {
  const today = todayDateInput()
  const [startDate, setStartDate] = useState(addDaysInput(new Date(), -29))
  const [endDate, setEndDate] = useState(today)
  const [expanded, setExpanded] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['project-value-dashboard', project.id, startDate, endDate],
    queryFn: () => workloadApi.getProjectValueDashboard(project.id, { startDate, endDate }),
  })

  const dashboard = data?.data?.data

  return (
    <section style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <div style={{ minWidth: 0 }}>
          <button type="button" onClick={() => setExpanded((value) => !value)} style={projectTitleButtonStyle}>
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span style={{ ...projectDotStyle, background: project.color }} />
            <h2 style={projectTitleStyle}>{project.name}</h2>
            <span style={projectKeyStyle}>{project.identifier}</span>
          </button>
        </div>
        <div style={dateControlsStyle}>
          <label style={dateLabelStyle}>
            Start
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={dateInputStyle} />
          </label>
          <label style={dateLabelStyle}>
            End
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={dateInputStyle} />
          </label>
        </div>
      </div>

      {isLoading || !dashboard ? (
        <div style={sectionLoadingStyle}>Loading project section...</div>
      ) : (
        <ProjectValueContent dashboard={dashboard} expanded={expanded} />
      )}
    </section>
  )
}

function ProjectValueContent({ dashboard, expanded }: { dashboard: ProjectValueDashboard; expanded: boolean }) {
  const chartMax = useMemo(() => {
    const maxValue = Math.max(...dashboard.employees.flatMap((employee) => [employee.estimatedHours, employee.actualHours]), 1)
    return Math.ceil(maxValue / 5) * 5
  }, [dashboard.employees])

  return (
    <div style={contentGridStyle}>
      <div style={summaryStripStyle}>
        <MetricPill icon={<TimerReset size={13} />} label="Estimated" value={formatHours(dashboard.totals.estimatedHours)} />
        <MetricPill icon={<Clock size={13} />} label="Actual" value={formatHours(dashboard.totals.actualHours)} />
        <MetricPill
          icon={<BarChart2 size={13} />}
          label="Variance"
          value={`${dashboard.totals.varianceHours > 0 ? '+' : ''}${formatHours(dashboard.totals.varianceHours)}`}
          tone={dashboard.totals.varianceHours > 0 ? '#dc2626' : '#16a34a'}
        />
        <MetricPill icon={<ListChecks size={13} />} label="Tasks" value={String(dashboard.totals.taskCount)} />
        <MetricPill icon={<Users size={13} />} label="Employees" value={String(dashboard.totals.employeeCount)} />
      </div>

      <div style={mainPanelStyle}>
        <div style={compactTitleRowStyle}>
          <h3 style={panelTitleStyle}>Employee Estimated vs Actual</h3>
          <div style={legendStyle}>
            <span style={legendItemStyle}><span style={{ ...legendSwatchStyle, background: '#0f766e' }} /> Estimated</span>
            <span style={legendItemStyle}><span style={{ ...legendSwatchStyle, background: '#5eead4' }} /> Actual</span>
          </div>
        </div>

        {dashboard.employees.length === 0 ? (
          <div style={chartEmptyStyle}>No employee estimates or time logs found for this project.</div>
        ) : (
          <div style={verticalChartStyle}>
            {dashboard.employees.map((employee) => (
              <EmployeeBar key={employee.user.id} employee={employee} chartMax={chartMax} />
            ))}
          </div>
        )}
      </div>

      {expanded && (
        <div style={overrunBlockStyle}>
          <div style={overrunTitleStyle}>Top overruns</div>
          {dashboard.topOverruns.length === 0 ? (
            <div style={overrunEmptyStyle}>No employee overruns in this range.</div>
          ) : (
            dashboard.topOverruns.map((employee) => (
              <div key={employee.user.id} style={overrunRowStyle}>
                <Avatar user={employee.user} size="sm" />
                <span style={overrunNameStyle}>{employee.user.fullName}</span>
                <strong style={overrunValueStyle}>+{formatHours(employee.varianceHours)}</strong>
              </div>
            ))
          )}
        </div>
      )}

      {expanded && (
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Assigned Tasks</th>
                <th style={thStyle}>Estimated</th>
                <th style={thStyle}>Actual</th>
                <th style={thStyle}>Variance</th>
                <th style={thStyle}>Health</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.employees.map((employee) => (
                <tr key={employee.user.id}>
                  <td style={tdStyle}>
                    <div style={employeeCellStyle}>
                      <Avatar user={employee.user} size="sm" />
                      <span>{employee.user.fullName}</span>
                    </div>
                  </td>
                  <td style={tdStyle}>{employee.assignedTasks}</td>
                  <td style={tdStyle}>{formatHours(employee.estimatedHours)}</td>
                  <td style={tdStyle}>{formatHours(employee.actualHours)}</td>
                  <td style={{ ...tdStyle, color: employee.varianceHours > 0 ? '#dc2626' : '#16a34a', fontWeight: 800 }}>
                    {employee.varianceHours > 0 ? '+' : ''}{formatHours(employee.varianceHours)}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...healthBadgeStyle, color: healthColor(employee.health), background: `${healthColor(employee.health)}14` }}>
                      {healthLabel(employee.health)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function EmployeeBar({ employee, chartMax }: { employee: ProjectValueEmployee; chartMax: number }) {
  return (
    <div style={employeeBarColumnStyle} title={`${employee.user.fullName}: estimated ${formatHours(employee.estimatedHours)}, actual ${formatHours(employee.actualHours)}`}>
      <div style={verticalBarsStyle}>
        <div style={verticalBarSlotStyle}>
          <div style={{ ...verticalBarFillStyle, height: `${Math.max((employee.estimatedHours / chartMax) * 100, employee.estimatedHours > 0 ? 4 : 0)}%`, background: '#0f766e' }} />
        </div>
        <div style={verticalBarSlotStyle}>
          <div style={{ ...verticalBarFillStyle, height: `${Math.max((employee.actualHours / chartMax) * 100, employee.actualHours > 0 ? 4 : 0)}%`, background: '#5eead4' }} />
        </div>
      </div>
      <div style={compactEmployeeNameStyle}>{employee.user.fullName}</div>
      <div style={compactHoursStyle}>{formatHours(employee.actualHours)}</div>
    </div>
  )
}

function MetricPill({ icon, label, value, tone = '#111827' }: { icon: React.ReactNode; label: string; value: string; tone?: string }) {
  return (
    <div style={metricPillStyle}>
      <span style={metricLabelStyle}>{icon}{label}</span>
      <strong style={{ color: tone, fontSize: 12 }}>{value}</strong>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  minHeight: '100%',
  padding: '24px',
  background: '#f7f8fb',
  boxSizing: 'border-box',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 18,
}

const eyebrowStyle: React.CSSProperties = {
  color: '#0f766e',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
}

const titleStyle: React.CSSProperties = {
  margin: '4px 0 6px',
  fontSize: 28,
  lineHeight: 1.15,
  color: '#111827',
}

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  color: '#64748b',
  fontSize: 14,
  maxWidth: 760,
  lineHeight: 1.5,
}

const projectStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
}

const sectionStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
  overflow: 'hidden',
}

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '16px 18px',
  borderBottom: '1px solid #e2e8f0',
  background: '#fbfdff',
}

const projectTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
}

const projectTitleButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  padding: 0,
  color: '#111827',
  cursor: 'pointer',
  textAlign: 'left',
}

const projectDotStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 999,
  flexShrink: 0,
}

const projectTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  color: '#111827',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const projectKeyStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  fontWeight: 800,
  color: '#64748b',
  background: '#f1f5f9',
  borderRadius: 6,
  padding: '3px 7px',
}

const dateControlsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 10,
}

const dateLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 11,
  fontWeight: 800,
  color: '#64748b',
  textTransform: 'uppercase',
}

const dateInputStyle: React.CSSProperties = {
  height: 34,
  border: '1px solid #dbe3ee',
  borderRadius: 6,
  padding: '0 10px',
  fontSize: 13,
  color: '#111827',
}

const contentGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '12px 18px',
}

const summaryStripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}

const mainPanelStyle: React.CSSProperties = {
  minWidth: 0,
}

const panelTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 14,
}

const compactTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 8,
}

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  color: '#111827',
}

const panelMetaStyle: React.CSSProperties = {
  marginTop: 3,
  color: '#64748b',
  fontSize: 12,
}

const legendStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 12,
  color: '#64748b',
  fontWeight: 700,
}

const legendItemStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}

const legendSwatchStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 3,
}

const verticalChartStyle: React.CSSProperties = {
  minHeight: 118,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(82px, 1fr))',
  alignItems: 'end',
  gap: 10,
  padding: '6px 0 0',
}

const employeeBarColumnStyle: React.CSSProperties = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
}

const verticalBarsStyle: React.CSSProperties = {
  height: 76,
  width: '100%',
  maxWidth: 54,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 5,
  alignItems: 'end',
}

const verticalBarSlotStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: 5,
  background: '#eef2f7',
  display: 'flex',
  alignItems: 'end',
  overflow: 'hidden',
}

const verticalBarFillStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: '5px 5px 0 0',
}

const compactEmployeeNameStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 96,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 800,
  color: '#111827',
}

const compactHoursStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 900,
  color: '#0f766e',
}

const metricPillStyle: React.CSSProperties = {
  minHeight: 28,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 7,
  background: '#f8fafc',
  fontSize: 12,
}

const metricLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  color: '#64748b',
  fontWeight: 700,
}

const overrunBlockStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '8px 10px',
  border: '1px solid #fee2e2',
  borderRadius: 8,
  background: '#fff7f7',
}

const overrunTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 900,
  color: '#64748b',
  textTransform: 'uppercase',
  marginRight: 2,
}

const overrunRowStyle: React.CSSProperties = {
  display: 'inline-grid',
  gridTemplateColumns: '24px minmax(0, 120px) auto',
  alignItems: 'center',
  gap: 8,
  padding: '2px 0',
}

const overrunNameStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 12,
  color: '#111827',
  fontWeight: 750,
}

const overrunValueStyle: React.CSSProperties = {
  color: '#dc2626',
  fontSize: 12,
}

const overrunEmptyStyle: React.CSSProperties = {
  color: '#94a3b8',
  fontSize: 12,
}

const tableWrapStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflow: 'hidden',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: '#f8fafc',
  color: '#64748b',
  textAlign: 'left',
  fontSize: 11,
  textTransform: 'uppercase',
  fontWeight: 900,
  borderBottom: '1px solid #e2e8f0',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: '#334155',
  borderBottom: '1px solid #eef2f7',
  fontWeight: 650,
}

const employeeCellStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: '#111827',
  fontWeight: 800,
}

const healthBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  borderRadius: 7,
  padding: '0 8px',
  fontSize: 11,
  fontWeight: 900,
}

const emptyStyle: React.CSSProperties = {
  padding: 48,
  textAlign: 'center',
  color: '#94a3b8',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  background: '#ffffff',
}

const sectionLoadingStyle: React.CSSProperties = {
  padding: 28,
  color: '#94a3b8',
  fontSize: 13,
}

const chartEmptyStyle: React.CSSProperties = {
  padding: 30,
  color: '#94a3b8',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  textAlign: 'center',
}
